import { PersonAvatarVariant } from '@factorialco/f0-react'
import { ExternalLink, Pencil, Plus, Settings } from '@factorialco/f0-react/icons/app'
import { PostsGroupHeaderQuery } from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'

import { buildAvatarUrl } from 'modules/posts/lib/buildAvatarUrl'

type Props = {
  group: PostsGroupHeaderQuery['posts']['group']
  isDynamicCommunitiesEnabled?: boolean
}

export const buildCommunityHeaderProps = ({
  group,
  isDynamicCommunitiesEnabled = false,
}: Props) => {
  if (!group) {
    return {
      title: '',
    }
  }

  const canLeaveGroup = group._policies?.leave?.can
  const canUpdateGroup = group._policies?.update.can && group.isMember

  const secondaryActions = [
    canUpdateGroup && {
      label: i18n.t('posts.communities.edit_group'),
      icon: Pencil,
      key: 'edit-community',
    },
  ].filter(Boolean)

  const otherActions = [
    canUpdateGroup && {
      label: i18n.t('posts.communities.add_members'),
      key: 'add-members',
    },
    canUpdateGroup && {
      label: i18n.t('posts.communities.remove_members'),
      key: 'remove-members',
    },
    canLeaveGroup && {
      label: i18n.t('posts.communities.leave_group'),
      key: 'leave-group',
    },
    canUpdateGroup && {
      label: i18n.t('posts.communities.delete_group'),
      key: 'delete-community',
      critical: true,
    },
  ].filter(Boolean)

  const primaryActionItems = [
    {
      label: i18n.t('posts.communities.invite_people'),
      value: 'invite-people',
    },
    isDynamicCommunitiesEnabled && {
      label: i18n.t('posts.communities.set_automatic_invites'),
      value: 'set-automatic-invites',
      icon: Settings,
      description: i18n.t('posts.communities.set_automatic_invites_description'),
    },
  ].filter(Boolean)

  const primaryAction =
    canUpdateGroup && primaryActionItems.length > 1
      ? {
          items: primaryActionItems,
          key: 'primary-dropdown',
        }
      : canUpdateGroup
      ? {
          label: i18n.t('posts.communities.invite_people'),
          icon: Plus,
          key: 'invite-people',
        }
      : !group.isMember
      ? {
          label: i18n.t('dashboard.groups.join.submit'),
          icon: Plus,
          key: 'join-group',
        }
      : null

  const allMembersAvatars: PersonAvatarVariant[] = group.employees.map((employee) => ({
    firstName: employee.firstName ?? '',
    lastName: employee.lastName ?? '',
    type: 'person' as const,
    src: buildAvatarUrl(employee.avatar),
  }))

  const metadata =
    allMembersAvatars.length > 0
      ? [
          {
            label: i18n.t('posts.communities.members'),
            value: {
              type: 'list' as const,
              variant: 'person' as const,
              avatars: allMembersAvatars,
            },
            actions: [
              {
                label: i18n.t('posts.communities.members'),
                icon: ExternalLink,
              },
            ],
          },
        ]
      : []

  return {
    title: group.title ?? '',
    description: group.description && group.description !== '-' ? group.description : '',
    status: {
      label: '',
      text: i18n.t('posts.communities.public'),
      variant: 'neutral' as const,
    },
    metadata,
    primaryAction,
    secondaryActions,
    otherActions,
  }
}
